// Client contract helpers for the views (DESIGN §9): form limits/defaults, handles, posting days, stage PICs and the
// contract progress line. They prefer the shared layer (derive.contractProgress / derive.stagePic, shared constants)
// and fall back to the same rules here, so a view never breaks on an older shared layer.
import * as derive from '../shared/derive.js';
import * as C from '../shared/constants.js';
import { monthNumberForDate } from '../shared/dates.js';

const FALLBACK_DAYS = [
  { key: 'mon', label: 'Mon' }, { key: 'tue', label: 'Tue' }, { key: 'wed', label: 'Wed' }, { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' }, { key: 'sat', label: 'Sat' }, { key: 'sun', label: 'Sun' },
];
export const WEEKDAYS = Array.isArray(C.WEEKDAYS) && C.WEEKDAYS.length === 7 ? C.WEEKDAYS : FALLBACK_DAYS;
const DAY_KEYS = WEEKDAYS.map((d) => d.key);

const spec = (s, min, max, def) => [s?.min ?? min, s?.max ?? max, s?.default ?? def];
const [BATCH_MIN, BATCH_MAX, BATCH_DEF] = spec(C.CONTRACT_BATCHES, 1, 18, 1);
const [MONTH_MIN, MONTH_MAX, MONTH_DEF] = spec(C.CONTRACT_MONTHS, 1, 24, 6);
const [CONTENT_MIN, CONTENT_MAX, CONTENT_DEF] = spec(C.CONTENT_PER_MONTH, 1, 200, C.DEFAULT_VIDEO_COUNT || 15);
export const LIMITS = { batches: [BATCH_MIN, BATCH_MAX], months: [MONTH_MIN, MONTH_MAX], content: [CONTENT_MIN, CONTENT_MAX] };
export const DEFAULTS = { batches: BATCH_DEF, months: MONTH_DEF, content: CONTENT_DEF };

const int = (v, [min, max], fallback) => {
  const n = Number(v);
  return Number.isInteger(n) && n >= min && n <= max ? n : fallback;
};
export const contractBatchesOf = (c) => int(c?.contractBatches, LIMITS.batches, DEFAULTS.batches);
export const contractMonthsOf = (c) => int(c?.contractMonths, LIMITS.months, DEFAULTS.months);
export const contentPerMonthOf = (c) => int(c?.contentPerMonth, LIMITS.content, DEFAULTS.content);
export const postingDaysOf = (c) => DAY_KEYS.filter((k) => Array.isArray(c?.postingDays) && c.postingDays.includes(k));
export const picOf = (c) => ({ script: c?.pic?.script || '', videographer: c?.pic?.videographer || '', editor: c?.pic?.editor || '' });

// "@name" / "instagram.com/name" / "tiktok.com/@name" → "name" (stored without "@"; same rule as the reducer).
export function cleanHandle(v) {
  let h = String(v ?? '').trim();
  const link = /^(?:https?:\/\/)?(?:[a-z]+\.)?(?:instagram|tiktok)\.com\/@?([^/?#]+)/i.exec(h);
  if (link) h = link[1];
  return h.replace(/^@+/, '');
}
const HANDLE_OK = new RegExp(`^[A-Za-z0-9._]{1,${C.HANDLE_MAX || 30}}$`);
export const handleValid = (h) => !h || HANDLE_OK.test(h);
export const instagramUrl = (h) => (h && HANDLE_OK.test(h) ? `https://instagram.com/${h}` : '');
export const tiktokUrl = (h) => (h && HANDLE_OK.test(h) ? `https://tiktok.com/@${h}` : '');

// ['mon','wed','fri'] → "Mon · Wed · Fri" ('' when none).
export function postingDaysText(days) {
  return WEEKDAYS.filter((d) => days?.includes(d.key)).map((d) => d.label).join(' · ');
}

// Which PIC works a stage (DESIGN §9): Brief Script + Script → script; stage 03 + Brief Videographer → videographer;
// Brief Video Editing, Video Editing, Result → editor. `key` = a stage key or a stage-03 brief key.
const STAGE_PIC = C.STAGE_PIC || { briefPremisScript: 'script', script: 'script', videoShootEditingBrief: 'videographer', videoEditing: 'editor', result: 'editor' };
const BRIEF_PIC = C.BRIEF_PIC || { videographer: 'videographer', editing: 'editor' };
export const PIC_LABELS = { script: 'Script PIC', videographer: 'Videographer PIC', editor: 'Editor PIC' };
export const picRoleOf = (key) => STAGE_PIC[key] || BRIEF_PIC[key] || '';

// Team id of the PIC for a stage (or brief) of a batch ('' when unset or no longer on the team).
export function stagePicId(batch, key, index) {
  let id = '';
  try {
    id = typeof derive.stagePic === 'function'
      ? derive.stagePic(batch, key, index)
      : picOf(index?.clientsById.get(batch?.clientId))[picRoleOf(key)] || '';
  } catch { id = ''; }
  return typeof id === 'string' && id && index?.teamById.has(id) ? id : '';
}

// Months 1…end where end = contractMonths + the off months inside it (an off month stretches the contract).
function effectiveEnd(index, client) {
  const off = new Set((index?.monthsByClient.get(client.id) || []).filter((m) => m.off === true).map((m) => m.monthNumber));
  let end = contractMonthsOf(client);
  for (let n = 1; n <= end && end < (C.MAX_MONTH || 99); n++) if (off.has(n)) end += 1;
  return { end, off: end - contractMonthsOf(client) };
}

// Contract progress of a client → { batch, batches, month, months, off, endMonth, started, text }
// text: "Batch 3 of 6 · Month 4 of 6 (+1 off)". derive.contractProgress when present; the same rules here otherwise
// (batch = the highest batch number, month = the date-based current month, end = contractMonths + off months).
export function clientContract(state, index, client, today) {
  if (!client) return null;
  if (typeof derive.contractProgress === 'function') {
    try {
      const p = derive.contractProgress(state, index, client.id, today);
      if (p) {
        return {
          batch: p.batchNow, batches: p.batchesTotal, month: p.monthNow, months: p.monthsTotal, off: p.offMonths,
          endMonth: p.effectiveMonths, started: p.started, text: p.text,
        };
      }
    } catch { /* fall through to the local rules */ }
  }
  const { end, off } = effectiveEnd(index, client);
  const batch = (index?.batchesByClient.get(client.id) || []).reduce((m, b) => Math.max(m, b.batchNumber || 0), 0);
  const started = !!client.startDate && !!today && today >= client.startDate;
  const month = started ? monthNumberForDate(client.startDate, today, C.MAX_MONTH || 99) : 1;
  const r = { batch, batches: contractBatchesOf(client), month, months: contractMonthsOf(client), off, endMonth: end, started };
  r.text = `Batch ${batch} of ${r.batches} · Month ${month} of ${r.months}${off ? ` (+${off} off)` : ''}`;
  return r;
}
